import { useEffect, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';

export const SHORTCUTS = {
  EXAMINE: 'e',
  INTERVIEW: 'i',
  TRAVEL: 't',
  READ: 'r',
  CONSULT: 'c',
  CASE_FILE: 'f',
  EVIDENCE_BOARD: 'b',
  SUSPECT_WALL: 'p',
  TIMELINE: 'l',
  HELP: '?',
  ESCAPE: 'Escape',
};

export const SHORTCUT_DESCRIPTIONS = [
  { key: 'E', action: 'Examine location' },
  { key: 'I', action: 'Interview suspect' },
  { key: 'T', action: 'Travel to location' },
  { key: 'R', action: 'Read document' },
  { key: 'C', action: 'Consult guide' },
  { key: 'F', action: 'Open Case File' },
  { key: 'B', action: 'Evidence Board' },
  { key: 'P', action: 'Suspect Wall' },
  { key: 'L', action: 'Timeline' },
  { key: '?', action: 'Show shortcuts' },
  { key: 'Esc', action: 'Close modal' },
];

/**
 * useKeyboardShortcuts - Hook for handling keyboard shortcuts in the game
 * 
 * @param {Object} handlers - Object containing handler functions
 * @param {function} handlers.onExamine - Called when E is pressed
 * @param {function} handlers.onInterview - Called when I is pressed
 * @param {function} handlers.onTravel - Called when T is pressed
 * @param {function} handlers.onRead - Called when R is pressed
 * @param {function} handlers.onConsult - Called when C is pressed
 * @param {function} handlers.onCaseFile - Called when F is pressed
 * @param {function} handlers.onEvidenceBoard - Called when B is pressed
 * @param {function} handlers.onSuspectWall - Called when P is pressed
 * @param {function} handlers.onTimeline - Called when L is pressed
 * @param {function} handlers.onHelp - Called when ? is pressed
 * @param {function} handlers.onEscape - Called when Escape is pressed
 * @param {boolean} enabled - Whether shortcuts are enabled (default: true)
 */
export const useKeyboardShortcuts = ({
  onExamine,
  onInterview,
  onTravel,
  onRead,
  onConsult,
  onCaseFile,
  onEvidenceBoard,
  onSuspectWall,
  onTimeline,
  onHelp,
  onEscape,
  enabled = true,
}) => {
  const gameStatus = useGameStore((state) => state.gameStatus);
  const keyboardShortcutsEnabled = useGameStore((state) => state.settings?.keyboardShortcutsEnabled ?? true);

  const handleKeyDown = useCallback((event) => {
    // Don't trigger if typing in an input or textarea
    if (
      event.target.tagName === 'INPUT' ||
      event.target.tagName === 'TEXTAREA' ||
      event.target.isContentEditable
    ) {
      return;
    }

    // Leave browser/OS combos (Cmd+S, Ctrl+P, Alt+...) to the browser
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    // Don't trigger if game not active or shortcuts disabled
    if (gameStatus !== 'in_progress' || !keyboardShortcutsEnabled) return;

    const key = event.key.toLowerCase();

    switch (key) {
      case SHORTCUTS.EXAMINE:
        event.preventDefault();
        onExamine?.();
        break;
      case SHORTCUTS.INTERVIEW:
        event.preventDefault();
        onInterview?.();
        break;
      case SHORTCUTS.TRAVEL:
        event.preventDefault();
        onTravel?.();
        break;
      case SHORTCUTS.READ:
        event.preventDefault();
        onRead?.();
        break;
      case SHORTCUTS.CONSULT:
        event.preventDefault();
        onConsult?.();
        break;
      case SHORTCUTS.CASE_FILE:
        event.preventDefault();
        onCaseFile?.();
        break;
      case SHORTCUTS.EVIDENCE_BOARD:
        event.preventDefault();
        onEvidenceBoard?.();
        break;
      case SHORTCUTS.SUSPECT_WALL:
        event.preventDefault();
        onSuspectWall?.();
        break;
      case SHORTCUTS.TIMELINE:
        event.preventDefault();
        onTimeline?.();
        break;
      case SHORTCUTS.HELP:
        event.preventDefault();
        onHelp?.();
        break;
      case 'escape':
        onEscape?.();
        break;
      default:
        break;
    }
  }, [
    gameStatus,
    keyboardShortcutsEnabled,
    onExamine,
    onInterview,
    onTravel,
    onRead,
    onConsult,
    onCaseFile,
    onEvidenceBoard,
    onSuspectWall,
    onTimeline,
    onHelp,
    onEscape,
  ]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
};

export default useKeyboardShortcuts;
